/**
 * Roll Shade Calculator Service
 * Handles all pricing and calculation logic for Made.bb roll shades
 */

import type { CalculationResult, MadebbSettings } from '../types/madebb.types';

export class RollShadeCalculatorService {
  private settings: MadebbSettings;

  constructor(settings: MadebbSettings) {
    this.settings = settings;
  }

  /**
   * Calculate fabric yardage needed based on dimensions
   */
  calculateFabricYardage(width: number, drop: number): number {
    const fabricRollWidth = this.settings.fabricRollWidth;
    let yardage = 0;

    if (width <= 44) {
      // Scenario 1: Width ≤ 44", yardage = total length (drop + 10" to wrap around 2x2)
      yardage = (drop + 10) / 36; // Convert inches to yards
    } else if (width > 44 && drop <= 44) {
      // Scenario 2: Width > 44" but drop ≤ 44", yardage = width
      yardage = width / 36; // Convert inches to yards
    } else {
      // Scenario 3: Both width > 44" and drop > 44"
      // Yardage = (width in yards) × (drop + 10" for wrap / 44"), rounded up
      const widthInYards = width / 36;
      const dropMultiplier = (drop + 10) / 44; // Add 10" for 2x2 wrap
      yardage = Math.ceil(widthInYards * dropMultiplier);
    }

    // Add wastage percentage
    yardage = yardage * (1 + this.settings.wastagePercentage);

    // Round up to nearest 0.1 yards
    return Math.ceil(yardage * 10) / 10;
  }

  /**
   * Calculate all costs for a roll shade order
   * @param materialCostPerYard - Optional override for fabric cost from inventory
   */
  calculateCosts(
    width: number,
    drop: number,
    material: string,
    materialCostPerYard?: number
  ): CalculationResult | null {
    if (!width || !drop || !material) return null;

    const { pricing, componentCosts } = this.settings;

    // Calculate fabric yardage and cost
    const fabricYardage = this.calculateFabricYardage(width, drop);
    // Use inventory price if provided, otherwise use default
    const fabricCostPerYard = materialCostPerYard ?? pricing.fabricCostPerYard;
    const fabricCost = fabricYardage * fabricCostPerYard;

    // Pine costs
    const pineCutLength = (width - 1) / 12; // Convert inches to feet
    const pineCost = pineCutLength * componentCosts.pine2x2PerFoot;

    // Bottom pipe cost
    const bottomPipeCutLength = (width - 1) / 12; // Convert inches to feet
    const bottomPipeCost = bottomPipeCutLength * componentCosts.sch40PipePerFoot;

    // Mounting boxes (2 per unit)
    const mountingBoxCost = 2 * componentCosts.mountingBoxPerUnit;

    // Mounting screws (8 per unit based on BOM)
    const mountingScrewsCost = 8 * componentCosts.mountingScrewsPerUnit;

    // Name plate and screws
    const namePlateCost = 1 * componentCosts.namePlatePerUnit;
    const namePlateScrewsCost = 2 * componentCosts.namePlateScrewsPerUnit;

    // Roll pulley system (varies by width)
    const needsRollPulleys = width > 48; // More than 4 feet needs 3 pulleys, otherwise 2
    const pulleyCount = needsRollPulleys ? 3 : 2;
    const rollPulleyBoxCost = pulleyCount * componentCosts.rollPulleyBoxPerUnit;
    const rollPulleyWheelCost = pulleyCount * componentCosts.rollPulleyWheelPerUnit;
    const rollPulleyCoverCost = pulleyCount * componentCosts.rollPulleyCoverPerUnit;

    // Stainless wood screws for pulleys
    const stainlessWoodScrewsCost = pulleyCount * 2 * componentCosts.stainlessWoodScrewPerUnit;

    // M3 capscrews for pulleys
    const m3CapScrewCost = pulleyCount * 1 * componentCosts.m3CapScrewPerUnit;

    // Pull cord (length varies by drop)
    const pullCordLength = Math.ceil((drop + 24) / 12); // Drop + 24 inches for operation, convert to feet
    const pullCordCost = pullCordLength * componentCosts.pullCordPerFoot;

    // Pull cord accessories
    const pullCordEndCost = 1 * componentCosts.pullCordEndPerUnit;
    const pullCordRetainerCost = 1 * componentCosts.pullCordRetainerPerUnit;

    // Hardware bag and box
    const hardwareBagCost = 1 * componentCosts.hardwareBagPerUnit;
    const boxCost = 1 * componentCosts.boxPerUnit;

    // Labor calculation
    const baseLaborHours = pricing.baseLaborHours;
    const additionalLaborMinutes = width + drop; // 1 minute per inch of width and drop
    const totalLaborHours = baseLaborHours + (additionalLaborMinutes / 60);
    const laborCost = totalLaborHours * pricing.laborRatePerHour;

    // Calculate total production cost
    const productionCost = fabricCost + pineCost + bottomPipeCost + mountingBoxCost +
      mountingScrewsCost + namePlateCost + namePlateScrewsCost + rollPulleyBoxCost +
      rollPulleyWheelCost + rollPulleyCoverCost + stainlessWoodScrewsCost +
      m3CapScrewCost + pullCordCost + pullCordEndCost + pullCordRetainerCost +
      hardwareBagCost + boxCost + laborCost;

    // Bulk Rate: Manufacturing cost + markup
    const bulkRate = productionCost * pricing.bulkMarkup;

    // Retail Price: Bulk Rate + markup, rounded up to nearest *.99
    const retailBase = bulkRate * pricing.retailMarkup;
    const price = Math.floor(retailBase / 10) * 10 + 9.99;

    return {
      fabricCost,
      fabricYardage,
      pineCost,
      bottomPipeCost,
      laborCost,
      totalLaborHours,
      pulleyCount,
      pullCordLength,
      productionCost,
      bulkRate,
      price
    };
  }

  /**
   * Validate input dimensions
   */
  validateDimensions(width: number, drop: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (width < 18 || width > 120) {
      errors.push('Width must be between 18 and 120 inches');
    }

    if (drop < 8 || drop > 120) {
      errors.push('Drop must be between 8 and 120 inches');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

/**
 * Default settings - will be loaded from backend in production
 */
export const defaultSettings: MadebbSettings = {
  pricing: {
    fabricCostPerYard: 40,
    laborRatePerHour: 25,
    bulkMarkup: 1.25,
    retailMarkup: 1.40,
    baseLaborHours: 1.5
  },
  orderCounter: 1,
  componentCosts: {
    pine2x2PerFoot: 1.50,
    sch40PipePerFoot: 2.00,
    mountingBoxPerUnit: 3.00,
    mountingScrewsPerUnit: 0.50,
    namePlatePerUnit: 1.50,
    namePlateScrewsPerUnit: 0.50,
    rollPulleyBoxPerUnit: 2.00,
    rollPulleyWheelPerUnit: 1.00,
    rollPulleyCoverPerUnit: 1.00,
    stainlessWoodScrewPerUnit: 0.50,
    m3CapScrewPerUnit: 0.50,
    pullCordPerFoot: 3.00,
    pullCordEndPerUnit: 1.00,
    pullCordRetainerPerUnit: 1.00,
    hardwareBagPerUnit: 0.25,
    boxPerUnit: 5.00
  },
  fabricRollWidth: 46,
  wastagePercentage: 0.10,
  metadata: {
    created: '2025-01-20T00:00:00Z',
    version: '1.0.0'
  }
};
